import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { ContactForm } from "./ContactForm";
import { TrendingUp, Shield, Zap, Users, Globe, CheckCircle, BookOpen, DollarSign, BarChart3, RefreshCw, Calendar, Linkedin } from "lucide-react";
// import oussamaProfile from 'figma:asset/65f7dc3ee3d7715b661f54247b19d61e2e28612a.png';

interface InvestorPageProps {
  setCurrentPage: (page: string) => void;
}

export function InvestorPage({ }: InvestorPageProps) {
  const handleScheduleCall = () => {
    window.open(
      'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2TJ6HymvLK-zARw4aTR4bOhuZQ1HdF-fLXm58HoMdzHxTdJRbu9-FLSnCWnRUF95zZIBcJ-eCk?gv=true',
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <section className="relative bg-gradient-to-br from-[#e6f2ff] via-white to-gray-50 pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="text-lg md:text-xl text-[#004aad] mb-4 tracking-wide">
              MusliFin, Your Muslim Financing Partner
            </div>
            <h1 className="text-4xl md:text-6xl mb-6 text-gray-900">
              Rewiring Finance for 5M North American Muslims
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              We're building halal finance rails — starting with auto loans and scaling up.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <ContactForm 
                defaultValues={{
                  reason: "investor",
                  message: "Please Send Investor Pitch deck"
                }}
              >
                <Button 
                  size="lg" 
                  className="bg-[#004aad] hover:bg-[#003a8a] text-white px-8 py-4 text-lg"
                >
                  Request Deck
                </Button>
              </ContactForm>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={handleScheduleCall}
                className="border-[#004aad] text-[#004aad] hover:bg-[#e6f2ff] px-8 py-4 text-lg flex items-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                Schedule a Call
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works (Investors) Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">How It Works for Investors</h2>
            <p className="text-xl text-gray-600">A clear path from learning to earning with ethical investments</p>
          </div>
          
          <div className="grid lg:grid-cols-5 md:grid-cols-3 sm:grid-cols-2 gap-8">
            {/* Step 1 */}
            <Card className="p-6 text-center border-2 border-[#ccdfff] hover:border-[#99c7ff] transition-colors">
              <div className="w-12 h-12 bg-[#004aad] rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg mb-3 text-gray-900">Learn About the Opportunity</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Receive pitch deck</li>
                <li>• Intro call with founders</li>
                <li>• Understand Murabaha &amp; Musharaka models</li>
              </ul>
            </Card>

            {/* Step 2 */}
            <Card className="p-6 text-center border-2 border-[#ccdfff] hover:border-[#99c7ff] transition-colors">
              <div className="w-12 h-12 bg-[#004aad] rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg mb-3 text-gray-900">Understand the Capital Model</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Capital funds Shariah-compliant vehicle purchases</li>
                <li>• Asset-backed contracts</li>
                <li>• No interest involved</li>
              </ul>
            </Card>

            {/* Step 3 */}
            <Card className="p-6 text-center border-2 border-[#ccdfff] hover:border-[#99c7ff] transition-colors">
              <div className="w-12 h-12 bg-[#004aad] rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg mb-3 text-gray-900">Risk &amp; Return Management</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Pre-screened clients</li>
                <li>• Down payment and contract structure safeguards</li>
                <li>• Predictable, fixed returns</li>
              </ul>
            </Card>

            {/* Step 4 */}
            <Card className="p-6 text-center border-2 border-[#66afff] hover:border-[#99c7ff] transition-colors">
              <div className="w-12 h-12 bg-[#66afff] rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg mb-3 text-gray-900">Coming Soon: MusliInvest (2026)</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Browse live deals</li>
                <li>• Monitor repayment flows</li>
                <li>• Access Shariah audit reports</li>
                <li>• Reinvest or withdraw</li>
              </ul>
            </Card>

            {/* Step 5 */}
            <Card className="p-6 text-center border-2 border-[#ccdfff] hover:border-[#99c7ff] transition-colors">
              <div className="w-12 h-12 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-6 h-6 text-[#004aad]" />
              </div>
              <h3 className="text-lg mb-3 text-gray-900">Capital Recycled at Maturity</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Typical contract durations: 3–5 years</li>
                <li>• Fixed return profile</li>
                <li>• Option to recycle capital</li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* The Opportunity Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">The Opportunity</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl mb-6 text-gray-900">
                5M+ Muslims in US &amp; Canada. $30B+ auto finance opportunity. No trusted provider.
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                The North American Muslim population represents a massive, underserved market with strong purchasing power and clear financial needs.
              </p>
              <p className="text-lg text-gray-600">
                Current solutions are either non-compliant, overly expensive, or simply don't exist. MusliFin is building the infrastructure to serve this market properly.
              </p>
            </div>
            
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg w-full">
              <h4 className="text-xl mb-8 text-gray-900 text-center">Market Gap Visualization</h4>
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                  <span className="text-gray-600 text-sm md:col-span-4 lg:col-span-3">Total Muslim Population</span>
                  <div className="flex items-center md:col-span-6 lg:col-span-7">
                    <div className="flex-1 h-4 bg-[#ccdfff] rounded-full mr-4">
                      <div className="w-full h-4 bg-[#004aad] rounded-full transition-all duration-500"></div>
                    </div>
                  </div>
                  <span className="text-gray-900 text-sm font-medium md:col-span-2 md:text-right">5M+</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                  <span className="text-gray-600 text-sm md:col-span-4 lg:col-span-3">Auto Finance Need</span>
                  <div className="flex items-center md:col-span-6 lg:col-span-7">
                    <div className="flex-1 h-4 bg-[#ccdfff] rounded-full mr-4">
                      <div className="w-4/5 h-4 bg-[#004aad] rounded-full transition-all duration-500"></div>
                    </div>
                  </div>
                  <span className="text-gray-900 text-sm font-medium md:col-span-2 md:text-right">$30B+</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                  <span className="text-gray-600 text-sm md:col-span-4 lg:col-span-3">Compliant Providers</span>
                  <div className="flex items-center md:col-span-6 lg:col-span-7">
                    <div className="flex-1 h-4 bg-red-200 rounded-full mr-4">
                      <div className="w-2 h-4 bg-red-600 rounded-full transition-all duration-500"></div>
                    </div>
                  </div>
                  <span className="text-gray-900 text-sm font-medium md:col-span-2 md:text-right">~0</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product - Sayaara Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">Product: Sayaara</h2>
            <p className="text-xl text-gray-600">Our flagship auto financing platform</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl mb-6 text-gray-900">Plug-and-Play for Dealerships</h3>
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">Plug-and-play integration for dealerships</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">Murabaha &amp; Musharaka contract structures</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">Fully managed backend and compliance</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#004aad] w-5 h-5" />
                  <span className="text-gray-700">Real-time credit decisions</span>
                </div>
              </div>
              <p className="text-gray-600">
                Zero overhead for dealerships. Complete Shariah compliance. Seamless customer experience.
              </p>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="bg-white rounded-lg p-6 shadow-sm relative overflow-hidden">
                {/* Watermark overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="transform rotate-[-20deg] text-gray-200/30 text-4xl font-bold select-none">
                    SAMPLE DATA
                  </div>
                </div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg text-gray-900">Sayaara Dashboard Preview</h4>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">Example Figures</span>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Active Applications</span>
                      <span className="text-[#004aad]">247</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Approved This Month</span>
                      <span className="text-[#004aad]">89%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Average Approval Time</span>
                      <span className="text-[#004aad]">2.3 min</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Volume</span>
                      <span className="text-[#004aad]">$12.4M</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 italic">* Dashboard preview with illustrative data</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure Flywheel Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">Our Infrastructure Flywheel</h2>
            <p className="text-xl text-gray-600">Building the complete halal finance ecosystem</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-center border-2 border-[#004aad]">
              <div className="w-16 h-16 bg-[#004aad] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-xl">1</span>
              </div>
              <h3 className="text-xl mb-4 text-gray-900">Originate</h3>
              <p className="text-gray-600 mb-2">Auto Finance (2025)</p>
              <p className="text-sm text-gray-500">Sayaara platform launches</p>
            </Card>
            
            <Card className="p-8 text-center border-2 border-[#66afff]">
              <div className="w-16 h-16 bg-[#66afff] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-xl">2</span>
              </div>
              <h3 className="text-xl mb-4 text-gray-900">Serve</h3>
              <p className="text-gray-600 mb-2">MusliServe (Late 2026)</p>
              <p className="text-sm text-gray-500">Payment & Debt Servicing Rails</p>
            </Card>
            
            <Card className="p-8 text-center border-2 border-[#ccdfff]">
              <div className="w-16 h-16 bg-[#ccdfff] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-gray-600 text-xl">3</span>
              </div>
              <h3 className="text-xl mb-4 text-gray-900">Capitalize</h3>
              <p className="text-gray-600 mb-2">MusliInvest (Mid 2026)</p>
              <p className="text-sm text-gray-500">Investment platform</p>
            </Card>
          </div>
          

        </div>
      </section>

      {/* Why Invest Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">Why Invest in MusliFin</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <Zap className="w-12 h-12 text-[#004aad] mx-auto mb-4" />
              <h3 className="text-lg mb-3 text-gray-900">Clear Wedge</h3>
              <p className="text-gray-600 text-sm">
                Serving an underserved market with proven demand and no competition.
              </p>
            </Card>
            
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <TrendingUp className="w-12 h-12 text-[#004aad] mx-auto mb-4" />
              <h3 className="text-lg mb-3 text-gray-900">Scalable Revenue</h3>
              <p className="text-gray-600 text-sm">
                Recurring revenue model with high margins and network effects.
              </p>
            </Card>
            
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <Shield className="w-12 h-12 text-[#004aad] mx-auto mb-4" />
              <h3 className="text-lg mb-3 text-gray-900">Compliant</h3>
              <p className="text-gray-600 text-sm">
                Legal and Shariah compliance built into every product from day one.
              </p>
            </Card>
            
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <Users className="w-12 h-12 text-[#004aad] mx-auto mb-4" />
              <h3 className="text-lg mb-3 text-gray-900">Trusted Team</h3>
              <p className="text-gray-600 text-sm">
                Experienced founders with deep market knowledge and strong execution.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Meet the Founders Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-6 text-gray-900">Meet the Founding Team</h2>
            <p className="text-xl text-gray-600">Building the future of ethical finance</p>
          </div>
          
          <div className="flex justify-center max-w-4xl mx-auto">
            <Card className="p-8 text-center max-w-md">
              <div className="w-24 h-24 mx-auto mb-6">
                <img 
                  src="/assets/oussama-hidane-profile.png.png" 
                  alt="Oussama Hidane, Founder & CEO of MusliFin" 
                  className="w-24 h-24 rounded-full object-cover border-2 border-[#ccdfff]"
                />
              </div>
              <h3 className="text-xl mb-2 text-gray-900">Oussama Hidane</h3>
              <p className="text-[#004aad] mb-4">Founder &amp; CEO</p>
              <p className="text-gray-600 text-sm mb-6">
                Former Tech M&amp;A Analyst with experience in D&amp;O underwriting and financial modeling. Founded MusliFin to build ethical, Shariah-compliant finance solutions for underserved Muslim communities. Focused on trust, impact, and scalable fintech.
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('https://www.linkedin.com/in/oussama-hidane/', '_blank', 'noopener,noreferrer')}
                className="border-[#004aad] text-[#004aad] hover:bg-[#e6f2ff] flex items-center gap-2 mx-auto"
              >
                <Linkedin className="w-4 h-4" />
                View LinkedIn Profile
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-[#004aad]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl mb-6 text-white">
            We're currently raising — join us in building ethical infrastructure
          </h2>
          <p className="text-xl text-[#ccdfff] mb-8">
            Partner with us to create the financial products the Muslim community deserves.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <ContactForm 
              defaultValues={{
                reason: "investor",
                message: "Please Send Investor Pitch deck"
              }}
            >
              <Button 
                size="lg" 
                className="bg-white text-[#004aad] hover:bg-gray-100 px-8 py-4 text-lg"
              >
                Request Deck
              </Button>
            </ContactForm>

            <Button 
              size="lg" 
              variant="outline" 
              onClick={handleScheduleCall}
              className="border-white !text-white hover:bg-[#0056cc] hover:border-[#0056cc] !bg-transparent px-8 py-4 text-lg flex items-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              Schedule a Call
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}